import * as turf from '@turf/turf';
import L, {
  GeoJSON,
  latLng,
  LatLng,
  LatLngBounds,
  LayerGroup,
  Marker,
  marker,
} from 'leaflet';
import { Proj4GeoJSONFeature } from 'proj4leaflet';
import { createSearchResultGeometry } from './map.model';

/**
 * Fraction of the viewport kept clear around the edges so a repositioned label
 * is never flush against the border of the map.
 */
const VIEWPORT_LABEL_PADDING = -0.1;

/** Base css class applied to every highlight label tooltip. */
export const HIGHLIGHT_LABEL_CLASS = 'vicnames-highlight-label';

/** Additional css class applied when the label can be clicked. */
export const HIGHLIGHT_LABEL_CLICKABLE_CLASS =
  'vicnames-highlight-label--clickable';

export function createSearchResultGeometryWithLabel(
  geoJson: Proj4GeoJSONFeature,
  label: string,
  onLabelClick?: () => void,
) {
  const layer = createSearchResultGeometry(geoJson);
  if (label) {
    bindHighlightLabel(layer, label, onLabelClick);
  }
  return layer;
}

/**
 * Extract a short display label from a potentially long VICNAMES name string.
 */
export function shortenHighlightLabel(label: string): string {
  const pairs: { name: string; status: string }[] = [];
  let cursor = 0;

  while (cursor < label.length) {
    const close = label.indexOf(')', cursor);
    if (close === -1) {
      break;
    }

    const open = label.lastIndexOf('(', close);
    if (open < cursor) {
      cursor = close + 1;
      continue;
    }

    const status = label.slice(open + 1, close).trim();
    if (!/^[A-Z][A-Z ]*$/.test(status)) {
      cursor = close + 1;
      continue;
    }

    const name = label
      .slice(cursor, open)
      .replace(/^[\s,]+/, '')
      .replace(/<br\s*\/?>/gi, '')
      .trim();

    if (name) {
      pairs.push({ name, status });
    }

    cursor = close + 1;
  }

  if (pairs.length > 0) {
    return (
      pairs.find((pair) => pair.status === 'REGISTERED')?.name ?? pairs[0].name
    );
  }

  return label.trim();
}

/**
 * Bind a permanent tooltip label to a GeoJSON highlight layer.
 * - For point geometries: positions below the marker with a small offset.
 * - For line/polygon geometries: midpoint along
 *   the shape so the label sits directly on the geometry.
 *   The label follows the map so it stays visible for as long as any part of
 *   the shape is within the viewport.
 *
 * When `onLabelClick` is supplied the label becomes interactive and invokes the
 * callback when clicked, without letting the click through to the map.
 */
export function bindHighlightLabel(
  layer: L.GeoJSON,
  label: string,
  onLabelClick?: () => void,
) {
  label = shortenHighlightLabel(label);
  const sublayers = (layer as LayerGroup).getLayers();
  const firstFeature = (sublayers[0] as GeoJSON & { feature?: GeoJSON.Feature })
    ?.feature;
  const geomType: string | undefined = firstFeature?.geometry?.type;
  const isPoint = geomType === 'Point' || geomType === 'MultiPoint';

  const tooltipOptions: L.TooltipOptions = {
    permanent: true,
    direction: isPoint ? 'bottom' : 'center',
    className: onLabelClick
      ? `${HIGHLIGHT_LABEL_CLASS} ${HIGHLIGHT_LABEL_CLICKABLE_CLASS}`
      : HIGHLIGHT_LABEL_CLASS,
    interactive: !!onLabelClick,
    ...(isPoint ? { offset: L.point(0, 4) } : {}),
  };

  if (isPoint) {
    layer.bindTooltip(label, tooltipOptions);
    bindLabelClick(layer, onLabelClick);
  } else {
    const shape = layer.toGeoJSON() as GeoJSON.FeatureCollection;
    const shapeBounds = layer.getBounds();
    const center = getShapeCenter(layer);
    if (center) {
      const anchorMarker = marker(center, {
        opacity: 0,
        interactive: false,
      });
      anchorMarker.bindTooltip(label, tooltipOptions);
      bindLabelClick(anchorMarker, onLabelClick);
      keepLabelInViewport(anchorMarker, shape, shapeBounds, center);
      anchorMarker.addTo(layer);
    }
  }
}

/**
 * Forward clicks on an interactive tooltip to the supplied callback. Leaflet
 * bubbles tooltip events to their source layer, so listening on the source is
 * enough. Propagation is stopped so the map does not also handle the click.
 */
function bindLabelClick(source: L.Layer, onLabelClick?: () => void) {
  if (!onLabelClick) {
    return;
  }
  source.on('click', (event: L.LeafletMouseEvent) => {
    // Passing the Leaflet event (not the DOM event) makes Leaflet flag the
    // underlying DOM event as stopped, which prevents the map from also
    // handling the click.
    L.DomEvent.stopPropagation(event as unknown as Event);
    onLabelClick();
  });
}

/** Map events during which the label should keep following the viewport. */
const FOLLOW_EVENTS = 'move moveend zoomend viewreset';

/**
 * Keep the label within the viewport while the user pans and zooms.
 * The anchor is only moved once it is about to leave the screen, so the label
 * stays put during small movements and slides along during larger ones.
 * The label is hidden entirely when no part of the shape is in the viewport.
 */
function keepLabelInViewport(
  anchorMarker: Marker,
  shape: GeoJSON.FeatureCollection | GeoJSON.Feature,
  shapeBounds: LatLngBounds,
  shapeCenter: LatLng,
) {
  let map: L.Map | undefined;
  let frameRequested = false;
  let labelShown = true;

  const reposition = () => {
    frameRequested = false;
    if (!map) {
      return;
    }

    const bounds = map.getBounds();
    if (!shapeBounds.isValid() || !shapeBounds.intersects(bounds)) {
      anchorMarker.closeTooltip();
      labelShown = false;
      return;
    }

    // Cheap exit: the label is already on screen with room to spare.
    if (
      labelShown &&
      bounds.pad(VIEWPORT_LABEL_PADDING).contains(anchorMarker.getLatLng())
    ) {
      return;
    }

    const position = getVisibleLabelPosition(shape, bounds, shapeCenter);
    if (!position) {
      anchorMarker.closeTooltip();
      labelShown = false;
      return;
    }

    anchorMarker.setLatLng(position);
    anchorMarker.openTooltip();
    labelShown = true;
  };

  /* Panning fires `move` on every frame, so coalesce the work per frame. */
  const scheduleReposition = () => {
    if (frameRequested) {
      return;
    }
    frameRequested = true;
    L.Util.requestAnimFrame(reposition);
  };

  anchorMarker.on('add', () => {
    map = (anchorMarker as unknown as { _map?: L.Map })._map;
    map?.on(FOLLOW_EVENTS, scheduleReposition);
    labelShown = true;
    reposition();
  });

  anchorMarker.on('remove', () => {
    map?.off(FOLLOW_EVENTS, scheduleReposition);
    map = undefined;
  });
}

/**
 * Find where to place the label for the given viewport.
 * - Keeps the label on the shape centre while that centre is comfortably in view.
 * - Otherwise places it on the part of the shape that is currently visible.
 * - Returns `undefined` when the shape is not in the viewport at all.
 */
export function getVisibleLabelPosition(
  shape: GeoJSON.FeatureCollection | GeoJSON.Feature,
  bounds: LatLngBounds,
  shapeCenter: LatLng,
): LatLng | undefined {
  const clippable = getClippableFeatures(shape);
  if (clippable.length === 0) {
    return bounds.contains(shapeCenter) ? shapeCenter : undefined;
  }

  const paddedBounds = bounds.pad(VIEWPORT_LABEL_PADDING);
  if (paddedBounds.contains(shapeCenter)) {
    return shapeCenter;
  }

  const visible =
    clipToBounds(clippable, paddedBounds) ?? clipToBounds(clippable, bounds);
  if (!visible) {
    return undefined;
  }

  if (visible.every(isLineFeature)) {
    const midpoint = getLineMidpoint(visible);
    if (midpoint) {
      return midpoint;
    }
  }
  return getPointOnFeatures(visible);
}

type ClippableFeature = GeoJSON.Feature<
  | GeoJSON.LineString
  | GeoJSON.MultiLineString
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon
>;

function getClippableFeatures(
  shape: GeoJSON.FeatureCollection | GeoJSON.Feature,
): ClippableFeature[] {
  const features =
    shape.type === 'FeatureCollection' ? shape.features : [shape];
  return features.filter(isClippableFeature);
}

function isClippableFeature(
  feature: GeoJSON.Feature,
): feature is ClippableFeature {
  const type = feature.geometry?.type;
  return (
    type === 'LineString' ||
    type === 'MultiLineString' ||
    type === 'Polygon' ||
    type === 'MultiPolygon'
  );
}

function isLineFeature(feature: ClippableFeature) {
  const type = feature.geometry.type;
  return type === 'LineString' || type === 'MultiLineString';
}

/**
 * Clip the features to the viewport, returning only the parts that are visible.
 */
function clipToBounds(
  features: ClippableFeature[],
  bounds: LatLngBounds,
): ClippableFeature[] | undefined {
  const bbox: GeoJSON.BBox = [
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth(),
  ];

  const clipped = features.flatMap((feature) => {
    try {
      const result = turf.bboxClip(feature, bbox);
      return hasCoordinates(result) ? [result] : [];
    } catch {
      return [];
    }
  });

  return clipped.length > 0 ? clipped : undefined;
}

function hasCoordinates(feature: ClippableFeature): boolean {
  return feature.geometry.coordinates.length > 0;
}

/**
 * Midpoint of the longest visible line, so labels on roads stay on the road.
 */
function getLineMidpoint(features: ClippableFeature[]): LatLng | undefined {
  try {
    const lines = features
      .flatMap((feature) => turf.flatten(feature).features)
      .filter(
        (feature): feature is GeoJSON.Feature<GeoJSON.LineString> =>
          feature.geometry.type === 'LineString',
      );

    if (lines.length === 0) {
      return undefined;
    }

    const longest = lines.reduce(
      (best, current) =>
        turf.length(current) > turf.length(best) ? current : best,
      lines[0],
    );
    const midpoint = turf.along(longest, turf.length(longest) / 2);
    return toLatLng(midpoint);
  } catch {
    return undefined;
  }
}

function getPointOnFeatures(features: ClippableFeature[]): LatLng | undefined {
  try {
    return toLatLng(turf.pointOnFeature(turf.featureCollection(features)));
  } catch {
    return undefined;
  }
}

function toLatLng(point: GeoJSON.Feature<GeoJSON.Point>): LatLng {
  const [lng, lat] = point.geometry.coordinates;
  return latLng(lat, lng);
}

/**
 * Guarantees the result is on the actual geometry
 */
function getShapeCenter(layer: L.GeoJSON): LatLng | undefined {
  const geoJsonData = layer.toGeoJSON() as
    | GeoJSON.FeatureCollection
    | GeoJSON.Feature;

  try {
    const point = turf.pointOnFeature(geoJsonData as turf.AllGeoJSON);
    const [lng, lat] = point.geometry.coordinates;
    return latLng(lat, lng);
  } catch {
    // Fall back to Leaflet bounds center
    const bounds = layer.getBounds();
    return bounds.isValid() ? bounds.getCenter() : undefined;
  }
}
